/**
 * Sentry filtering utilities.
 *
 * Add patterns to IGNORED_ERROR_PATTERNS to suppress known,
 * non-actionable errors from being reported to Sentry.
 * Each entry accepts a plain substring or a RegExp.
 */
const IGNORED_ERROR_PATTERNS = [
  // Session timeout — handled by the auth flow (redirects to login with a
  // warning). Occurs in all supported UI languages.
  "token has expired",
  "inactividad",
  "Inaktivität",
  "inactivité"
];

const LOOPBACK_IPV4_PATTERN =
  /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/;

/**
 * Returns true when Sentry should report errors for the current runtime.
 * @param {string} nodeEnvironment
 * @param {string} hostname
 * @returns {boolean}
 */
export function isSentryEnabled(nodeEnvironment, hostname) {
  if (nodeEnvironment !== "production" || typeof hostname !== "string") {
    return false;
  }

  const normalizedHostname = hostname.toLowerCase().replace(/\.$/, "");
  const isLocalhost =
    normalizedHostname === "localhost" ||
    normalizedHostname.endsWith(".localhost");
  const isLoopback =
    normalizedHostname === "::1" ||
    normalizedHostname === "[::1]" ||
    LOOPBACK_IPV4_PATTERN.test(normalizedHostname);

  return Boolean(normalizedHostname) && !isLocalhost && !isLoopback;
}

/**
 * Returns true when an error string matches any ignored pattern.
 * @param {string} message
 * @returns {boolean}
 */
function matchesIgnoredPattern(message) {
  return IGNORED_ERROR_PATTERNS.some(pattern =>
    pattern instanceof RegExp
      ? pattern.test(message)
      : message.includes(pattern)
  );
}

/**
 * Sentry `beforeSend` callback.
 * Return null to drop the event, or the event to keep it.
 * @param {import("@sentry/react").Event} event
 * @returns {import("@sentry/react").Event | null}
 */
export function beforeSend(event) {
  const message = event.exception?.values?.[0]?.value || event.message || "";
  if (matchesIgnoredPattern(message)) {
    return null;
  }
  return event;
}

/**
 * Returns true when an unhandled rejection reason should be
 * silently ignored (not forwarded to Sentry).
 * @param {unknown} reason - The rejection reason from the PromiseRejectionEvent
 * @returns {boolean}
 */
export function isIgnoredRejection(reason) {
  const message = reason instanceof Error ? reason.message : String(reason);
  return matchesIgnoredPattern(message);
}
